import { useEffect } from 'react'

/**
 * useOutsideClick
 * Attaches a mousedown (or configurable) listener to the document and invokes the handler
 * when the event target lies outside the provided element ref.
 *
 * Contract:
 *  - ref: React ref object whose .current points at the interactive root element.
 *  - active: when false, no listeners are registered.
 *  - handler: called once per outside interaction.
 *  - options: { eventType?: string } allow customizing event type (default 'mousedown').
 *
 * Edge cases handled:
 *  - ref.current undefined: listener early returns.
 *  - Rapid mount/unmount: cleanup guaranteed.
 *  - Nested portals: if element not contained, still counts as outside.
 */
export function useOutsideClick(
  ref: React.RefObject<HTMLElement | null>,
  handler: (ev: Event) => void,
  active: boolean,
  options?: { eventType?: string }
) {
  useEffect(() => {
    if (!active) return
    const eventType = options?.eventType || 'mousedown'

    const listener = (e: Event) => {
      const el = ref.current
      if (!el) return
      if (el.contains(e.target as Node)) return // click inside => ignore
      handler(e)
    }

    document.addEventListener(eventType, listener) // bubble phase so stopPropagation in children works
    return () => {
      document.removeEventListener(eventType, listener)
    }
  }, [ref, handler, active, options?.eventType])
}

export default useOutsideClick
